import { styled } from "styled-components";
import { cond, propEq, stubTrue } from "lodash/fp";
import { rgba, padding } from "polished";
const SelectButtonButton = styled("button")(
  ({ theme }) => ({
    backgroundColor: "white",
    border: `1px solid ${theme.controlBorderColor}`,
    borderRadius: 2,
    color: theme.color.gray8,
    fontFamily: theme.fontFamily.default,
    fontSize: theme.fontSize.default,
    fontWeight: theme.fontWeight.normal,
    lineHeight: theme.lineHeight.default,
    minWidth: 0,
    outline: 0,
    padding: 0,
    textAlign: "left",
    transition: "all .3s cubic-bezier(0.645, 0.045, 0.355, 1)",
    width: "100%",
    '&[aria-expanded="true"]': {
      borderColor: theme.color.blue5,
      boxShadow: `0 0 0 2px ${rgba(theme.color.blue6, 0.2)}`
    },
    '&[aria-disabled="true"]': {
      "&, &:active, &:focus, &:hover": {
        backgroundColor: theme.color.gray3,
        borderColor: theme.controlBorderColor,
        boxShadow: "none",
        color: theme.color.gray7,
        pointerEvents: "none",
        "&::after": {
          content: "none"
        }
      }
    }
  }),
  cond([
    [
      propEq("$tone", "critical"),
      ({ theme }) => ({
        borderColor: theme.color.red6,
        "&:focus": {
          boxShadow: `0 0 0 2px ${rgba(theme.color.red6, 0.25)}`
        },
        '&[aria-expanded="true"]': {
          borderColor: theme.color.red6,
          boxShadow: `0 0 0 2px ${rgba(theme.color.red6, 0.25)}`
        }
      })
    ],
    [
      propEq("$appearance", "bare"),
      () => ({
        backgroundColor: "transparent",
        borderColor: "transparent",
        '&[aria-expanded="true"]': {
          backgroundColor: "transparent",
          borderColor: "transparent"
        }
      })
    ],
    [
      propEq("$appearance", "cell"),
      ({ $appearance, theme }) => ({
        backgroundColor: "transparent",
        borderRadius: 0,
        borderWidth: 0,
        "&::after": {
          bottom: 0,
          content: JSON.stringify(""),
          display: "block",
          height: 0,
          position: "absolute"
        },
        "&:hover::after": {
          backgroundColor: theme.color.monochrome5,
          height: 2,
          left: 0,
          right: 0
        },
        '&[aria-expanded="true"]': {
          boxShadow: $appearance === "cell" ? "inset 0 0 0 2px rgba(0,0,0,0.05)" : "none",
          "&::after": {
            backgroundColor: theme.color.blue6,
            height: 3,
            left: 0,
            right: 0
          }
        },
        "&:active": {
          boxShadow: `inset 0 0 0 2px ${rgba(theme.color.blue6, 0.25)}`
        },
        "&:focus": {
          boxShadow: "inset 0 0 0 2px rgba(0,0,0,0.05)"
        }
      })
    ],
    [
      propEq("$appearance", "grid"),
      ({ theme }) => ({
        backgroundColor: "transparent",
        border: `1px solid ${theme.borderColor}`,
        borderRadius: 0,
        "&:hover": {
          borderColor: theme.color.blue5
        },
        "&:focus": {
          borderColor: theme.color.blue5,
          boxShadow: `0 0 0 2px ${rgba(theme.color.blue6, 0.2)}`
        }
      })
    ],
    [
      propEq("$appearance", "inline"),
      ({ theme }) => ({
        backgroundColor: "transparent",
        border: 0,
        "&:hover": {
          backgroundColor: theme.color.gray3
        },
        "&:active": {
          backgroundColor: theme.color.gray4
        },
        "&:focus": {
          boxShadow: `0 0 0 2px ${theme.color.gray3}`
        },
        '&[aria-expanded="true"]': {
          boxShadow: `0 0 0 2px ${rgba(theme.color.blue6, 0.2)}`
        }
      })
    ],
    [
      stubTrue,
      ({ theme }) => ({
        "&:hover": {
          borderColor: theme.color.blue5
        },
        "&:focus": {
          borderColor: theme.color.blue5,
          boxShadow: `0 0 0 2px ${rgba(theme.color.blue6, 0.2)}`
        }
      })
    ]
  ])
);
const SelectButtonOuter = styled("div")({
  display: "flex",
  position: "relative"
});
const SelectButtonInner = styled("span")(
  {
    alignItems: "center",
    display: "flex",
    outline: 0,
    paddingLeft: 11,
    paddingRight: 4
  },
  ({ $magnitude }) => ({
    large: {
      ...padding(5, null)
    },
    medium: {
      ...padding(3, null)
    },
    small: {
      ...padding(1, 7)
    }
  })[$magnitude],
  ({ $compact }) => $compact ? {
    padding: 0
  } : void 0
);
const SelectButtonText = styled("span")(
  {
    flex: "1 1 0",
    overflowX: "hidden",
    textAlign: "left",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  ({ $compact }) => !$compact ? {
    paddingRight: 8
  } : void 0,
  ({ theme, $isPlaceholder }) => $isPlaceholder ? {
    color: theme.color.gray6
  } : void 0
);
const SelectButtonPillInner = styled("span")({
  marginRight: 1
});
const SelectButtonPillOuter = styled("span")({
  marginLeft: 8
});
const SelectButtonIcon = styled("span")(
  ({ theme }) => ({
    color: theme.color.gray6,
    flexBasis: "auto",
    flexGrow: 0,
    flexShrink: 0
  }),
  ({ $compact }) => ({
    marginRight: $compact ? 0 : 7
  })
);
export {
  SelectButtonButton,
  SelectButtonIcon,
  SelectButtonInner,
  SelectButtonOuter,
  SelectButtonPillInner,
  SelectButtonPillOuter,
  SelectButtonText
};
